import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { LetDirective, PushPipe } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { Subscription, tap } from 'rxjs';

import { Theme } from '@ninety/platform/models/enums/theme';
import {
  FeatureFlagFacade,
  UserModelState,
  LogOutActions,
  selectIsBillingV2Company,
  selectCurrentUser,
  selectCurrentUserIsAdminOrOwner,
  selectHasUserSettingsAlerts,
  selectShowReferAFriend,
} from '@ninety/platform/state';
import * as HelpfulSelectors from '@ninety/platform/state/app-global/helpful-permissions/helpful-permissions.selectors';
import { TerraAvatarModule, TerraIconModule } from '@ninety/terra';
import {
  DisplayNameOrEmailPipe,
  UserInitialsPipe,
  POPOVER_TOKEN,
  PopoverService,
  NinetyChipComponent,
} from '@ninety/ui/core';

import { UserProfileMenuAnalyticsActions } from './user-profile-menu-analytics.actions';

@Component({
  selector: 'ninety-user-profile-menu',
  templateUrl: 'user-profile-menu.component.html',
  styleUrls: ['user-profile-menu.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    RouterModule,
    LetDirective,
    PushPipe,
    TerraIconModule,
    TerraAvatarModule,
    UserInitialsPipe,
    DisplayNameOrEmailPipe,
    NinetyChipComponent,
  ],
})
export class UserProfileMenuComponent implements OnInit, OnDestroy {
  private readonly featureFlagFacade = inject(FeatureFlagFacade);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly popoverRef = inject<PopoverService>(POPOVER_TOKEN);

  /**
   * if disabled is true, show different styles and don't allow navClick
   */
  @Input() disabled = false;

  protected readonly referFriendEnabled$ = this.store.select(selectShowReferAFriend);

  readonly Theme = Theme; // theme enum for use in HTML
  private userSubscription: Subscription;
  public user: UserModelState;

  readonly isHelpful$ = this.store.select(HelpfulSelectors.selectHasAnyHelpfulPermission);
  readonly hasPartnerPermission$ = this.store.select(HelpfulSelectors.selectHasPartnerPermissions);
  protected billingUrl: string;
  private isBillingV2Company = false;
  protected hasSettingsAlerts$ = this.store.select(selectHasUserSettingsAlerts);

  readonly isAdminOrOwner$ = this.store.select(selectCurrentUserIsAdminOrOwner);

  ngOnInit(): void {
    this.userSubscription = this.store
      .select(selectCurrentUser)
      .pipe(
        tap(user => {
          this.user = user;
        })
      )
      .subscribe();

    this.userSubscription = this.store
      .select(selectIsBillingV2Company)
      .pipe(
        tap(isBillingV2Company => {
          this.isBillingV2Company = isBillingV2Company;
        })
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
  }

  itemClicked(url: string): void {
    /*
      Changed from using routerLink to this to handle auxiliary routes.
      For some reason loading this component in the PopoverService doesn't allow us to override the detail outlet.
      I wasn't able to find another way to get this to work. 😔 )
    */
    this.router.navigateByUrl(url);
    this.close();
  }

  close() {
    this.popoverRef.close();
  }

  referAFriendClicked(): void {
    this.store.dispatch(UserProfileMenuAnalyticsActions.referAFriendClicked());
    this.itemClicked('settings/user/profile?tab=referral');
  }

  openReferralUrl() {
    window.open(
      'https://www.ninety.io/refer-a-friend?utm_source=cs_referral&utm_campaign=cs_referral&utm_id=cs_referral',
      '_blank'
    );
    this.close();
  }

  logout() {
    this.store.dispatch(LogOutActions.initiateLogOut());
    this.close();
  }
}
